/**
 * Status field formatter
 *
 * Displays the order's confirmation status. The editable value is `isConfirmedOrder`
 * (toggled via the order confirmation section), so when the user changes the confirmation
 * the original status is crossed out and the new confirmation status is highlighted, like
 * any other changeable field.
 */

import {useOrderProcessContext} from '@hconnect/tuc-processes/context/useOrderProcessContext'
import {useIsConfirmed} from '@hconnect/tuc-processes/fields/isConfirmed/useIsConfirmed'
import {useChangeableField} from '@hconnect/tuc-processes/orderManagement/orderChange'
import {OrderDto, OrderStatus} from '@hconnect/tuc-processes/orderManagement/types'

import type {FieldFormatter} from './types'
import {createValueArray} from './utils'

export const useStatusFormatter = (): FieldFormatter => {
  const {processData} = useOrderProcessContext<OrderDto>()
  const {isConfirmed} = useIsConfirmed()
  const confirmationState = useChangeableField({field: 'isConfirmedOrder'})

  return (context) => {
    const {t} = context
    const {orderStatus} = processData

    const hasChanges = confirmationState.isDirty
    const requiresApproval = hasChanges && confirmationState.state === 'requireDispatchConfirmation'

    // Map a confirmation boolean to the matching order status label.
    const formatConfirmationStatus = (confirmed: boolean): string =>
      t(
        `orderManagement.orderStatusLabel.${confirmed ? OrderStatus.Confirmed : OrderStatus.Pending}`,
        confirmed ? 'confirmed' : 'unconfirmed'
      )

    // Old value: the order's current status label (preserves non-confirmation statuses).
    const oldValue = orderStatus
      ? t(`orderManagement.orderStatusLabel.${orderStatus}`, orderStatus)
      : null
    // New value: when the confirmation changed, reflect the new confirmation status.
    const newValue = hasChanges ? formatConfirmationStatus(Boolean(isConfirmed)) : oldValue

    const values = createValueArray(
      oldValue,
      newValue,
      hasChanges,
      t('orderIntake.rmc.orderManagement.notSpecified', 'Not specified')
    )

    return {values, requiresApproval, hasChanges}
  }
}
